import * as common from "./common";
import BaseSync, { ISyncAddSet } from "./base";
import { IModifiedType } from "./sync.types";
import * as logger from "../utils/logger";
import { getServerDate } from "../utils/storage/date";
import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import { TripAnnotationActions } from "../store/tripAnnotations/actions";
import TripAnnotation, { ITripAnnotation } from "../model/TripAnnotation";
import { getDeviceIdsFromTrips, getStartOfDayFrom } from "../utils/trip/tripHelper";
import { extend, isEqual } from "../utils/vanilla";
import { ISyncInterval } from "../store/mobile/types";
import { TMultiCallParam } from "../utils/api.interfaces";
import { isDuplicateException, isOverLimitException } from "../utils/errorHandler/error.types";
import { MeasureName } from "../utils/performanceProfiler/measureName";
import PerformanceProfileBatcher from "../utils/performanceProfiler/performanceProfileBatcher";

export default class TripAnnotationSync extends BaseSync {
    private syncInterval?: ISyncInterval;

    synchronize(progressCb: common.TSyncCallback, syncInterval?: ISyncInterval, profilingBatcher?: PerformanceProfileBatcher): Promise {
        return this.createProfiledSync(MeasureName.SyncTripAnnotation, () => {
            this.syncInterval = syncInterval;

            // Last sync might have failed due to rate limiting. Don't repeat those calls
            if (this.didSyncFailRecently()) {
                logger.event(logger.Event.SYNCTRIPANNOTATIONCONFLICT, "Skip TripAnnotation sync because previous run failed very recently");
                progressCb("TripAnnotation");
                return Promise.resolve();
            }

            let handleSyncSuccess = () => this.updateRunStateAtSyncComplete();

            let handleSyncError = error => this.updateRunStateAtSyncComplete(error);

            return new Promise(resolve => {
                this.fetchData()
                    .then(serverTripAnnotations => this.mergeTripAnnotations(serverTripAnnotations))
                    .then((tripAnnotations) => {
                        this.setTripAnnotations(tripAnnotations);
                        progressCb("TripAnnotation");
                        handleSyncSuccess();
                        resolve();
                    })
                    .catch(err => {
                        progressCb("TripAnnotation", err);
                        handleSyncError(err);
                        resolve();
                    });
            });
        }, profilingBatcher);
    }

    fetchData(): Promise<IIdDictionary<IIdDictionary<TripAnnotation>>> {
        return new Promise((resolve, reject) => {
            let tripsForCurrentUser = this.store.getState().trips[this.api.getUser()?.id || ""];
            let { fromDate, toDate } = this.getFromAndToDate();
            let deviceIds = getDeviceIdsFromTrips(fromDate, toDate, tripsForCurrentUser);
            let tripAnnotationGetCalls: TMultiCallParam[] = deviceIds.map(deviceId => ["Get", { typeName: "TripAnnotation", search: { deviceSearch: { id: deviceId }, fromDate, toDate } }]);

            if (tripAnnotationGetCalls.length > 0) {
                this.api.multiCall(
                    tripAnnotationGetCalls,
                    results => {
                        this.annotationsGetSuccessCallback(results, deviceIds, resolve);
                    },
                    err => reject(err)
                );
            } else {
                resolve({});
            }
        });
    }

    resolveConflict(serverTripAnnotations: IIdDictionary<IIdDictionary<TripAnnotation>>, localTripAnnotations: IIdDictionary<IIdDictionary<TripAnnotation>>) {
        let currentUser = this.api.getUser();
        Object.keys(serverTripAnnotations).forEach(deviceId => {
            let serverAnnotations = serverTripAnnotations[deviceId];
            let localAnnotations = localTripAnnotations[deviceId];
            if (localAnnotations) {
                Object.keys(serverAnnotations).forEach(id => {
                    let server = new TripAnnotation(serverAnnotations[id]);
                    let local = localAnnotations[id] ? new TripAnnotation(localAnnotations[id]) : undefined;
                    // Server log exists locally
                    if (local) {
                        // Conflict resolution
                        let previousState = local.previousState ? new TripAnnotation(local.previousState) : undefined;
                        let modifiedState = local.modified;
                        /*
                        Four scenarios:
                        local changed, server same => take local
                        local changed, server changed => conflict if local was "edit", merge local and server if local was "add"
                        local same, server changed => take server
                        local same, server same => do nothing
                        */
                        if (modifiedState) {
                            //Local changed
                            if (!isEqual(previousState, server)) {
                                if (modifiedState === "Add") {
                                    // Server has a different version, merge them
                                    logger.log(logger.Source.SYNC, "Merged local TripAnnotation with server", currentUser);
                                    localAnnotations[id] = common.merge<TripAnnotation>(localAnnotations[id], server, { modified: "Set" });
                                } else {
                                    logger.error(logger.Source.SYNC, `SYNC: TripAnnotation conflict (both server and client changed). Local ID: ${ local.id }, Server ID: ${ server.id }`);
                                }
                            }
                        } else {
                            // Local same
                            if (!isEqual(local, server)) {
                                logger.log(logger.Source.SYNC, "Updated local TripAnnotation from server", currentUser);
                                localAnnotations[id] = serverAnnotations[id];
                            }
                        }
                    } else {
                        localAnnotations[id] = serverAnnotations[id];
                    }
                });
            } else {
                localTripAnnotations[deviceId] = serverTripAnnotations[deviceId];
            }
        });
        return localTripAnnotations;
    }

    prepareLocalAnnotationsForSync(localTripAnnotations: IIdDictionary<IIdDictionary<TripAnnotation>>) {
        let calls: [IModifiedType, ISyncAddSet<ITripAnnotation>][] = [];
        Object.keys(localTripAnnotations).forEach((deviceId: string) => {
            let localAnnotations = localTripAnnotations[deviceId];
            Object.keys(localAnnotations).forEach((id: string) => {
                let annotation = localAnnotations[id];

                if (!annotation.id) {
                    throw new Error("Attempting to sync a record without an ID");
                }

                if (annotation.modified) {
                    let sanitizedAnnotation = extend({}, annotation);
                    this.removeClientSideProperties(sanitizedAnnotation);

                    if (annotation.modified === "Add" || annotation.modified === "Set") {
                        if (annotation._syncCounter === undefined) {
                            annotation._syncCounter = 5;
                        }
                        if (annotation._syncCounter > 0) {
                            annotation._syncCounter--;
                            calls.push([annotation.modified, { typeName: "TripAnnotation", entity: sanitizedAnnotation }]);
                        }
                    }
                }
            });
        });
        return calls;
    }

    // Merge in server TripAnnotations with local TripAnnotations
    mergeTripAnnotations(serverTripAnnotations: IIdDictionary<IIdDictionary<TripAnnotation>>) {
        let localTripAnnotations = extend(true, {}, this.store.getState().tripAnnotations) as IIdDictionary<IIdDictionary<TripAnnotation>>;

        localTripAnnotations = this.resolveConflict(serverTripAnnotations, localTripAnnotations);

        let calls: [IModifiedType, ISyncAddSet<ITripAnnotation>][] = this.prepareLocalAnnotationsForSync(localTripAnnotations);

        return new Promise((resolve, reject) => {
            if (calls.length > 0) {
                logger.log(logger.Source.SYNC, common.formatCallForLogger("TripAnnotation", calls), this.api.getUser());
                let findAnnotation = (entityId) => {
                    for (let deviceId of Object.keys(localTripAnnotations)) {
                        if (localTripAnnotations[deviceId][entityId]) {
                            return localTripAnnotations[deviceId][entityId];
                        }
                    }
                    return undefined;
                };
                this.api.multiCall(calls, (results) => {
                    let annotationIdsToRemove: string[] = [];
                    let getLocalEntity = (entity: ITripAnnotation) => {
                        let localAnnotation = findAnnotation(entity.id);
                        if (localAnnotation?.modified === "Add") {
                            annotationIdsToRemove.push(entity.id);
                        }
                        return localAnnotation;
                    };
                    this.resolveSyncedEntities(calls, results, getLocalEntity);
                    if (annotationIdsToRemove.length > 0) {
                        this.fetchData()
                            .then(latestServerAnnotations => {
                                annotationIdsToRemove.forEach(id => {
                                    let localAnnotation = findAnnotation(id);
                                    if (localAnnotation) {
                                        delete localTripAnnotations[localAnnotation.device.id][id];
                                    }
                                });
                                resolve(this.resolveConflict(latestServerAnnotations, localTripAnnotations));
                            })
                            .catch(reject);
                    } else {
                        resolve(localTripAnnotations);
                    }
                }, errorObject => {
                    if (isOverLimitException(errorObject)) {
                        reject(errorObject);
                        return;
                    }
                    let call = calls[errorObject.data.requestIndex];
                    let annotationId = call && call[1].entity.id;
                    let localAnnotation = annotationId && findAnnotation(annotationId);

                    if (isDuplicateException(errorObject) && localAnnotation) {
                        // This annotation is already in the database, make it a "Set" and sync again
                        localAnnotation.modified = "Set";
                        this.mergeTripAnnotations(serverTripAnnotations).then(resolve).catch(reject);
                    } else {
                        reject(errorObject);
                    }
                });
            } else {
                resolve(localTripAnnotations);
            }
        });
    }

    private setTripAnnotations(tripAnnotations: IIdDictionary<IIdDictionary<TripAnnotation>>) {
        Object.keys(tripAnnotations).forEach(deviceId => {
            this.store.dispatch(TripAnnotationActions.setTripAnnotations({ deviceId: deviceId, tripAnnotations: tripAnnotations[deviceId] }));
        });
    }

    private getFromAndToDate() {
        let syncInterval = this.syncInterval || this.store.getState().mobile.tripCategorizationSyncInterval;
        let fromDate = (syncInterval && syncInterval.fromDate) || getStartOfDayFrom(2).toISOString();
        let toDate = (syncInterval && syncInterval.toDate) || getServerDate().toISOString();
        return { fromDate, toDate };
    }

    private annotationsGetSuccessCallback(results: any, deviceIds: string[], resolve: any) {
        let tripAnnotations: IIdDictionary<IIdDictionary<TripAnnotation>> = {};
        let annotations = results.reduce((dict, entityArr) => {
            if (entityArr && entityArr.length > 0) {
                dict[entityArr[0].device.id] = entityArr;
            }
            return dict;
        }, {});

        // Create IIdDictionary<IIdDictionary<TripAnnotation>> keyed by device ID
        deviceIds.forEach(deviceId => {
            let annotationDict: IIdDictionary<TripAnnotation> = {};
            annotations[deviceId]?.forEach(tripAnnotationEntity => {
                annotationDict[tripAnnotationEntity.id] = new TripAnnotation(tripAnnotationEntity);
            });
            tripAnnotations[deviceId] = annotationDict;
        });
        resolve(tripAnnotations);
    }
}